/**
 * Frontend mirror of the backend country-geocode registry
 * (AddressService::COUNTRY_GEOCODE_PROFILES). It decides the Google Maps JS loader's
 * `language` + `region` for the interactive map + Places Autocomplete, so a workspace
 * in a configured country (e.g. Israel) enters/sees addresses in its own language.
 *
 * `region` is a BIAS, never a filter — it only prefers in-country results when
 * ambiguous; a user in any country can still search for and pick a foreign address.
 *
 * A country NOT listed here (and any workspace with no country) returns null, and the
 * loader is left exactly as it was (no language/region) — so nothing changes for an
 * unconfigured country. To add a country later, add one row here (matching the backend).
 */
export interface GeocodeLocale {
  /** Google Maps JS `language` (result script). */
  language: string;
  /** Google Maps JS `region` (ccTLD bias, non-blocking). */
  region: string;
}

const COUNTRY_LOCALES: Record<string, GeocodeLocale> = {
  IL: { language: "iw", region: "IL" },
};

/** Locale for an ISO-2 country code, or null when unconfigured / unknown. */
export function localeForCountry(countryCode?: string | null): GeocodeLocale | null {
  if (!countryCode) return null;
  return COUNTRY_LOCALES[countryCode.toUpperCase()] ?? null;
}

/**
 * The locale to boot the Maps loader with, read SYNCHRONOUSLY from the persisted
 * workspace country (authSaga writes it on /me). Read once at first render so the
 * loader options stay stable for the session — changing them after mount triggers
 * `useJsApiLoader`'s "different options" warning. A full reload (which reloads the
 * Maps script anyway) is what picks up a changed country.
 */
export function bootGeocodeLocale(): GeocodeLocale | null {
  try {
    return localeForCountry(localStorage.getItem("workspace_country_code"));
  } catch {
    return null;
  }
}
